import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Droplets, Clock } from "lucide-react";
import CTASection from "../components/CTASection";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About | Pool Guru Treasure Coast";
  }, []);

  const values = [
    { icon: Clock, title: "20+ Years Experience", body: "Two decades of serving Treasure Coast homeowners with reliable pool care." },
    { icon: Users, title: "Family Owned", body: "A local family business that treats your pool like our own." },
    { icon: Award, title: "Detailed Reporting", body: "Before/after photos and complete chemistry readings every single visit." },
    { icon: Droplets, title: "Florida Pool Experts", body: "We know exactly how Florida's climate affects your pool." },
  ];

  return (
    <div data-testid="about-page">
      <section className="bg-gradient-to-b from-pink-50/50 to-white py-20">
        <div className="section-container">
          <div className="overline mb-3">About Us</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 max-w-4xl">
            A family-owned pool care company built on <span className="text-brand-pink">trust, detail & consistency</span>.
          </h1>
        </div>
      </section>

      {/* BRAND STORY VIDEO — click to play */}
      <section className="bg-gradient-to-b from-brand-navy via-[#0f2847] to-brand-navy text-white py-16" data-testid="about-video-section">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <div className="overline mb-3 text-brand-pink">Watch Our Story</div>
            <h2 className="text-3xl md:text-4xl font-bold !text-white">
              See why families across the Treasure Coast call The Pool Guru
            </h2>
          </div>
          <div className="max-w-md mx-auto rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
            <video
              data-testid="about-brand-story-video"
              className="w-full h-auto block"
              controls
              preload="metadata"
              playsInline
              poster="https://customer-assets.emergentagent.com/job_0a06a0d5-58a4-4a28-a223-abff303efd5e/artifacts/lf7vi7ho_Screen%20Shot%202026-05-14%20at%2012.23.08%20PM.png"
            >
              <source
                src="https://customer-assets.emergentagent.com/job_0a06a0d5-58a4-4a28-a223-abff303efd5e/artifacts/vpysf54a_88.2-invideo-video_tools.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="section-container grid lg:grid-cols-2 gap-12 items-center">
          <div className="prose-content">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-6 text-brand-navy">
              The story behind the pink truck
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              For over 20 years, The Pool Guru has been caring for pools across Port St. Lucie, Fort Pierce, Stuart,
              and the entire Treasure Coast. What started as a small family business has grown into one of the most
              trusted pool service companies in the region — but we've stayed true to the values that got us here.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              Every pool we service gets the same attention to detail, the same thorough chemistry balancing, and
              the same before/after photo documentation that gives homeowners complete peace of mind. No shortcuts.
              No excuses. Just consistently clean, crystal-clear pools.
            </p>
            <p className="text-lg text-slate-700 leading-relaxed">
              Whether you own a small in-ground pool in Tradition, a luxury resort pool in PGA Village, or a
              vacation rental in Jensen Beach — we bring the same professional, reliable care to every visit.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://img1.wsimg.com/isteam/ip/6251f5ed-3ec2-463f-9bc9-248d796842b9/ChatGPT%20Image%20Apr%2017%2C%202026%2C%2004_41_51%20PM.png/:/rs=w:1280"
              alt="The Pool Guru service truck"
              className="w-full h-full object-cover aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="section-container">
          <div className="max-w-2xl mb-12">
            <div className="overline mb-3">What We Stand For</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold">Our values guide every visit.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-7 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-xl bg-brand-pink/10 flex items-center justify-center mb-4">
                  <v.icon className="w-6 h-6 text-brand-pink" />
                </div>
                <h3 className="text-lg font-bold mb-2">{v.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
